import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useState, useEffect } from 'react';

import { type DocketEntriesResponse } from '../../types';

import { useBaseCourtApi } from './useBaseCourtApi';

export const DOCKET_ENTRIES_QUERY_OPTIONS = {
  retry: 3, // Number of retries
  retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000), // Exponential backoff with 30s max
  staleTime: 2 * 60 * 1000, // 2 minutes
  cacheTime: 10 * 60 * 1000, // 10 minutes
  refetchOnWindowFocus: false,
};

/**
 * Custom hook for fetching docket entries data with pagination support
 * @param docketId - The ID of the docket to fetch entries for
 * @param page - Optional page number for pagination
 * @returns Query result with docket entries data and loading state
 */
export const useDocketEntriesData = (docketId: string, page: number = 1) => {
  const { getDocketEntries, isLoading: isTokenLoading } = useBaseCourtApi();
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  // Use React Query to handle API calls with built-in caching and retry logic
  const {
    data: docketEntriesData,
    isLoading,
    error,
    isFetching,
  } = useQuery<DocketEntriesResponse>(
    ['docket-entries', docketId, page],
    async () => {
      if (!docketId) {
        return { count: 0, next: null, previous: null, results: [] };
      }
      return getDocketEntries(docketId, page);
    },
    {
      ...DOCKET_ENTRIES_QUERY_OPTIONS,
      enabled: !!docketId && !isTokenLoading,
      keepPreviousData: true, // Keep previous data while fetching new page
      onError: (err: unknown) => {
        console.error('Error fetching docket entries:', err);
      },
    }
  );

  // Update pagination loading state based on isFetching
  useEffect(() => {
    setIsPaginationLoading(isFetching && !isLoading);
  }, [isFetching, isLoading]);

  const docketEntries = docketEntriesData?.results || [];

  return {
    docketEntries,
    effectiveDocketEntries: docketEntriesData,
    isLoading: isLoading || isTokenLoading,
    isPaginationLoading,
    error,
    currentPage: page,
  };
};
